import debounce from 'lodash/debounce';
import { useCallback, useMemo, useState } from 'react';
import { useOpQuery } from 'utils/customHooks/useOpQuery';
import { useNavigateToOrg } from './useNavigateToOrg';
import { orgHasSubOrgFeature } from '../helpers/orgAccess';

const debounceTimeout = 300;

export const useOrgSwitcher = ({
  homePath,
  token,
  identityId,
  currentOrgId,
  setCurrentOrgId,
}: {
  homePath?: string;
  token: Api.Response['describeAccessToken'];
  identityId?: number;
  currentOrgId: string | null;
  setCurrentOrgId(orgId: string | null): void;
}) => {
  const [waitingForNavigate, setWaitingForNavigate] = useState(false);
  const [searchValue, setSearchValue] = useState<string | undefined>();

  // Get the identity scopes (org id is null)
  const identityScopes = token?.tokenScopeList?.find(
    (scope) => !scope.org?.id,
  )?.scope;

  // Check identity scopes to see if the user has any scopes that start with o: or o-basic:
  const hasOrgSwitchingIdentityScopes = identityScopes?.some((scope) =>
    /^(o:|o-basic:)/.test(scope),
  );

  // Check the token scope list for any orgs with the sub orgs feature where there are scopes that start with s-o: or s-o-video:
  const hasOrgSwitchingOrgScopes = token?.tokenScopeList?.some((scope) => {
    const scopeOrgHasSubOrgFeature = orgHasSubOrgFeature(scope.org);
    const scopeHasOrgSwitchingScopes = scope.scope?.some((s) =>
      /^(s-o:|s-o-video:)/.test(s),
    );

    return scopeOrgHasSubOrgFeature && scopeHasOrgSwitchingScopes;
  });

  const hasPermissionToCallOrgsEndpoint = Boolean(
    hasOrgSwitchingIdentityScopes || hasOrgSwitchingOrgScopes,
  );

  // Get the orgs using the current search value
  const { data: orgData, isPending: orgDataIsPending } = useOpQuery({
    apiEndpointName: 'listOrgs',
    parameters: [
      {
        limit: 50,
        filter: 'status:(A,F)',
        options: 'withPackages',
        ...(searchValue && { q: searchValue }),
      },
    ],
    select: (data) => data.json.data ?? [],
    enabled: hasPermissionToCallOrgsEndpoint,
  });

  // Handles org navigation when the selected org is changed
  const { isOrgSwitching } = useNavigateToOrg({
    orgId: currentOrgId ? Number(currentOrgId) : undefined,
    identityId,
    homePath,
    token,
    setWaitingForNavigate,
  });

  // Handlers for the org select

  // This will need updated as more apps are added
  const handleSelectChange = (value: string | string[]) => {
    if (Array.isArray(value)) return; // It wont be an array
    setWaitingForNavigate(true);
    // Trigger the org change code - requires getting information from the database so not
    // doing in this handler
    setCurrentOrgId(value);
  };

  // Handle Search
  const handleSearchChange = useCallback((value: string | string[]) => {
    if (!Array.isArray(value)) {
      // shouldnt ever be an array here
      setSearchValue(value);
    }
  }, []);

  const debouncedSetSearchValue = useMemo(
    () => debounce(handleSearchChange, debounceTimeout),
    [handleSearchChange],
  );

  return {
    currentOrgId,
    orgData,
    isPending: hasPermissionToCallOrgsEndpoint && orgDataIsPending,
    setSearchValue: debouncedSetSearchValue,
    isOrgSwitching: waitingForNavigate || isOrgSwitching,
    handleSelectChange,
  };
};
